import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import {
  LeadershipPosition,
  LeadershipAssignment,
  AvailableEmployee,
  CreateLeadershipAssignment
} from '@/services/OrganizationSetupService';

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const AssignLeaderPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const {
    leadershipPositions,
    leadershipHierarchy,
    availableEmployees,
    loading,
    error,
    successMessage,
    fetchLeadershipPositions,
    fetchAvailableEmployees,
    fetchLeadershipHierarchy,
    assignPosition,
    updateAssignment,
    resetMessages
  } = useOrganizationSetupStore();

  const [selectedPosition, setSelectedPosition] = useState<LeadershipPosition | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<AvailableEmployee | null>(null);
  const [reportsTo, setReportsTo] = useState<LeadershipAssignment | null>(null);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [bio, setBio] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [editingLeader, setEditingLeader] = useState<LeadershipAssignment | null>(null);

  useEffect(() => {
    fetchLeadershipPositions();
    fetchAvailableEmployees();
    fetchLeadershipHierarchy();
  }, []);

  useEffect(() => {
    if (isEditing && leadershipHierarchy.length > 0) {
      const leader = leadershipHierarchy.find(l => l.id === parseInt(id));
      if (leader) {
        setEditingLeader(leader);
        const position = leadershipPositions.find(p => p.id === leader.position_id);
        const reportsToLeader = leadershipHierarchy.find(l => l.id === leader.reports_to_id);

        setSelectedPosition(position || null);
        setSelectedEmployee({
          id: leader.employee_id,
          name: leader.employee_name,
          email: leader.employee_email
        } as AvailableEmployee);
        setReportsTo(reportsToLeader || null);
        setStartDate(leader.start_date ? dayjs(leader.start_date) : null);
        setBio(leader.bio || '');
      }
    }
  }, [isEditing, id, leadershipHierarchy, leadershipPositions]);

  const handleSubmit = async () => {
    if (!selectedPosition || !selectedEmployee) {
      setFormError('Please select both a position and an employee');
      return;
    }

    const data: CreateLeadershipAssignment = {
      position_id: selectedPosition.id,
      employee_id: selectedEmployee.id,
      reports_to_id: reportsTo?.id,
      start_date: startDate?.format('YYYY-MM-DD'),
      bio: bio || undefined
    };

    let success: boolean;
    if (isEditing && editingLeader) {
      success = await updateAssignment(editingLeader.id, data);
    } else {
      success = await assignPosition(data);
    }

    if (success) {
      navigate('/organization');
    }
  };

  const handleCancel = () => {
    navigate('/organization');
  };

  // Filter reports_to options based on position level hierarchy
  const reportsToOptions = leadershipHierarchy.filter(l => {
    // Exclude self when editing
    if (editingLeader && l.id === editingLeader.id) return false;

    // If no position selected yet, show all (will be filtered when position is chosen)
    if (!selectedPosition) return true;

    // CEO (level 1) should not report to anyone
    if (selectedPosition.position_level === 1) return false;

    // Only show leaders at HIGHER levels (lower level number)
    return l.position_level < selectedPosition.position_level;
  });

  // Filter out positions that are already assigned (e.g., if CEO is assigned, don't show CEO)
  const availablePositions = leadershipPositions.filter(
    position => !leadershipHierarchy.some(leader => leader.position_id === position.id)
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate('/organization')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            Organization
          </Link>
          <Typography color="text.primary">
            {isEditing ? 'Edit Leader' : 'Assign Leader'}
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{ mb: 2 }}
          >
            Back to Organization
          </Button>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {isEditing ? 'Edit Leadership Assignment' : 'Assign Leadership Position'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isEditing
              ? 'Update the leadership assignment details below'
              : 'Select a position and assign an employee to build your organization\'s leadership structure'}
          </Typography>
        </Box>

        {/* Form Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Position Selection */}
              <Autocomplete
                options={availablePositions}
                value={selectedPosition}
                onChange={(_, value) => {
                  setSelectedPosition(value);
                  // Clear reportsTo if the new position is CEO or if current reportsTo is invalid
                  if (value?.position_level === 1 ||
                      (reportsTo && value && reportsTo.position_level >= value.position_level)) {
                    setReportsTo(null);
                  }
                }}
                getOptionLabel={(option) => `${option.position_title} (${option.position_code})`}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props as any;
                  return (
                    <Box component="li" key={key} {...restProps}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: option.color_code,
                          mr: 2
                        }}
                      />
                      <Box>
                        <Typography variant="body1">{option.position_title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.position_code} - Level {option.position_level}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Position"
                    required
                    placeholder="Select a leadership position"
                    helperText="Choose the leadership role for this assignment"
                  />
                )}
                disabled={isEditing}
              />

              {/* Employee Selection */}
              <Autocomplete
                options={availableEmployees}
                value={selectedEmployee}
                onChange={(_, value) => setSelectedEmployee(value)}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props as any;
                  return (
                    <Box component="li" key={key} {...restProps} sx={{ display: 'flex', gap: 2 }}>
                      <Avatar
                        src={option.profile_photo}
                        sx={{ width: 40, height: 40 }}
                      >
                        {getInitials(option.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email} {option.department_name && `- ${option.department_name}`}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    required
                    placeholder="Search and select an employee"
                    helperText="Select the employee to assign to this position"
                  />
                )}
              />

              {/* Reports To */}
              <Autocomplete
                options={reportsToOptions}
                value={reportsTo}
                onChange={(_, value) => setReportsTo(value)}
                getOptionLabel={(option) => `${option.employee_name} (${option.position_code})`}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props as any;
                  return (
                    <Box component="li" key={key} {...restProps} sx={{ display: 'flex', gap: 2 }}>
                      <Avatar
                        src={option.employee_photo}
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: option.color_code
                        }}
                      >
                        {getInitials(option.employee_name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{option.employee_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.position_title}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Reports To"
                    placeholder="Select manager (optional)"
                    helperText="Leave empty if this is the top of the hierarchy (e.g., CEO)"
                  />
                )}
              />

              {/* Start Date */}
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'When did this person start in this role?'
                  }
                }}
              />

              {/* Bio */}
              <TextField
                label="Bio"
                multiline
                rows={4}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="A short bio about this leader (optional)"
                helperText="This will be displayed on their profile card in the organization chart"
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ borderRadius: 2, px: 4 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !selectedPosition || !selectedEmployee}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (isEditing ? <SaveIcon /> : <PersonAddIcon />)}
                sx={{ borderRadius: 2, px: 4 }}
              >
                {loading ? 'Saving...' : (isEditing ? 'Update' : 'Assign Leader')}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={resetMessages}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={resetMessages} severity="success" variant="filled">
            {successMessage}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default AssignLeaderPage;
